/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Pressable from '@atlaskit/primitives/pressable';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup, { type PopupComponentProps } from '@atlaskit/popup';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import Spinner from '@atlaskit/spinner';

// eslint-disable-next-line import/no-cycle
import { BULK_DELETE_ACTION_NAME, EXIT_ACTION_NAME } from './BulkActionsMenu.tsx';

const styles = cssMap({
	button: {
		maxWidth: 'fit-content',
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral.bold'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
	},
	activeButton: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.bold.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.bold.pressed'),
		},
	},
	disabledButton: {
		pointerEvents: 'none',
	},
	disabledBox: {
		cursor: 'not-allowed',
	},
	inline: {
		height: '32px',
		font: token('font.body'),
		color: token('color.text.inverse'),
		paddingInline: token('space.100'),
	},
	limitDialog: {
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
		borderRadius: token('radius.medium'),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
	},
	popup: {
		borderRadius: token('radius.medium'),
		width: '665px',
	},
	deleteButton: {
		'&:hover': {
			backgroundColor: token('color.background.danger.bold.hovered'),
		},
	},
});

type BulkActionsButtonProps = {
	actionText: { id: string; defaultMessage: string; description: string };
	actionIcon?: ReactNode;
	actionName: string;
	count: number;
	onClick: () => void;
	isSelectionLimitReached?: boolean;
	actionLimit?: number;
	isLoading?: boolean;
	isDisabled?: boolean;
};

export const BulkActionsButton = ({
	actionText,
	actionIcon,
	actionName,
	count,
	onClick,
	isSelectionLimitReached = false,
	actionLimit = 0,
	isLoading = false,
	isDisabled = false,
}: BulkActionsButtonProps) => {
	const isButtonDisabled =
		actionName !== EXIT_ACTION_NAME && (count === 0 || isLoading || isDisabled);

	if (isSelectionLimitReached) {
		return (
			<Popup
				placement="right"
				isOpen
				content={() => (
					<Box xcss={cx(styles.limitDialog)}>
						<Inline alignBlock="center" space="space.100" xcss={cx(styles.inline)}>
							<FormattedMessage {...i18n.overTheLimitWarning} values={{ limit: actionLimit }} />
						</Inline>
					</Box>
				)}
				trigger={(triggerProps) => (
					<Box {...triggerProps} xcss={cx(styles.disabledBox)}>
						<Pressable xcss={cx(styles.button, styles.disabledButton)} aria-disabled>
							<Inline alignBlock="center" space="space.100" xcss={cx(styles.inline)}>
								<StatusWarningIcon label="" color={token('color.icon.warning')} />
								<FormattedMessage {...actionText} values={{ count }} />
							</Inline>
						</Pressable>
					</Box>
				)}
				popupComponent={CustomPopupContainer}
				offset={[0, -70]}
				shouldRenderToParent
			/>
		);
	}
	return (
		<Box xcss={cx(isButtonDisabled && styles.disabledBox)}>
			<Pressable
				onClick={onClick}
				xcss={cx(
					styles.button,
					styles.activeButton,
					isButtonDisabled && styles.disabledButton,
					actionName === BULK_DELETE_ACTION_NAME && styles.deleteButton,
				)}
				analyticsContext={{
					source: 'contentTreeBulkActions',
					attributes: {
						name: `${actionName}Button`,
						selectedItemCount: count,
					},
				}}
				aria-disabled={isButtonDisabled}
			>
				<Inline alignBlock="center" space="space.100" xcss={cx(styles.inline)}>
					{isLoading ? <Spinner size="small" appearance="invert" /> : actionIcon}
					<FormattedMessage {...actionText} values={{ count }} />
				</Inline>
			</Pressable>
		</Box>
	);
};

// Need custom popup container to allow us to override the border radius styling
const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ children, ...props }, ref) => (
		<div {...props} ref={ref} css={styles.popup}>
			{children}
		</div>
	),
);

const i18n = defineMessages({
	overTheLimitWarning: {
		id: 'page-tree.bulk-actions-menu.over-the-limit-warning',
		defaultMessage:
			'This action can only be applied to {limit} items at a time. Please deselect some items to continue.',
		description:
			'Warning message displayed when the user selects more than the allowed limit for a bulk action',
	},
});
